import React from 'react'
import { useIntl } from 'react-intl'
import { css } from '@emotion/css'
import { Stack } from '@contrast-security-inc/design-system-layouts'
import { Text } from '@contrast-security-inc/design-system-typography'
import { Card } from '@contrast-security-inc/design-system-surfaces'
import { getLanguageInfo } from './common/utils'
import { formatStyles } from '../../../../components/react-intl-helpers'
import {
  WizardStepContext,
  WizardStepContextValueType
} from './agent-wizard-steps'
import { AgentLanguage } from '../../../agent-language'
import { theme } from '@contrast-security-inc/design-system-foundations'
import { useSplitClient } from 'contrastBase/state'

// matching height/width with card below
// fragment strictly to satisfy ts

const languageCardCss = css`
  height: 176px;
  width: 172px;
  display: flex;
  flex-direction: column;
  align-items: center;
  background: white;
  padding: 12px;
  cursor: pointer;
`
const normalizeAgentLanguageCardCss = css`
  ${languageCardCss};
  margin: 12px;
`
const languageCardText = css`
  text-align: center;
  white-space: pre-wrap;
`
const languageCardDivCss = css`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  height: 100%;
  padding: 24px 0px;
  justify-content: start;
`
const AgentWizardLanguageCard = (props) => {
  const intl = useIntl()
  const {
    type,
    titleMsg,
    logo,
    currentLanguage,
    currentTypeOverride,
    setCurrentTypeOverride,
    setCurrentLanguage,
    defaultBranches
  } = props

  const ctx: WizardStepContextValueType = React.useContext(WizardStepContext)
  const onClickLanguage = React.useCallback(() => {
    let typeFinal = type
    if (type === 'KOTLIN' || type === 'SCALA') {
      setCurrentTypeOverride(type)
      typeFinal = 'JAVA'
    } else {
      setCurrentTypeOverride(null)
    }

    setCurrentLanguage(typeFinal)

    if (defaultBranches) {
      defaultBranches.forEach(([key, value]) => ctx.setBranch(key, value))
    } else {
      ctx.resetBranches()
    }
  }, [setCurrentTypeOverride, setCurrentLanguage, type, ctx, defaultBranches])

  let titleMsgFinal = titleMsg
  if (type === 'JAVA') {
    titleMsgFinal = 'title_language_normalizeJava'
  }

  const isSelected =
    (currentTypeOverride && currentTypeOverride === type) ||
    (!currentTypeOverride && type === currentLanguage)
      ? true
      : false

  // don't render unhandled languages (without languageInfo, ie proxy)
  return !type ? null : (
    <Card
      key={type}
      testId={`agent-wizard-language-card-${type}`}
      className={normalizeAgentLanguageCardCss}
      isSelected={isSelected}
      onSelect={onClickLanguage}
    >
      <div className={languageCardDivCss}>
        <img
          src={logo}
          alt={type}
          className={css`
            min-width: 64px;
            min-height: 64px;
            margin-bottom: 16px;
          `}
        />
        <Text as="div" font="body" className={languageCardText}>
          {intl.formatMessage({ id: titleMsgFinal }, { br: <br /> })}
        </Text>
      </div>
    </Card>
  )
}

const languageSelectCss = css`
  display: flex;
  flex-direction: row;
  gap: 12px;
  width: 64vw;
  flex-wrap: wrap;
  padding: 24px;
`
export const AgentWizardLanguageSelect = ({
  currentLanguage,
  setCurrentLanguage
}) => {
  const { isSplitTreatmentOn } = useSplitClient()

  const isDisplayRubyAgentInWizardEnabled = isSplitTreatmentOn(
    'PROD-3258_display_ruby_agent_in_wizard'
  )
  const intl = useIntl()
  const languages = !isDisplayRubyAgentInWizardEnabled
    ? AgentLanguage.getAllAsArray()
        .map((lang) => String(lang.getName()))
        .filter((it) => it !== 'RUBY')
    : AgentLanguage.getAllAsArray().map((lang) => String(lang.getName()))
  const [currentTypeOverride, setCurrentTypeOverride] = React.useState(null)
  // insert kotlin and scala, they depend on JAVA
  // ensure proper order, java, kotlin, scala, ..rest
  // these should really just come from the backend properly
  // theres quite a bit of complexity that could be removed here
  // if the backend just interpreted KOTLIN/SCALA as JAVA on its side
  const languagesFull = React.useMemo(() => {
    return languages
  }, [languages])

  return (
    <Stack flexDirection="column" testId="agent-wizard-language-select">
      <div className={languageSelectCss}>
        {languagesFull.map((lang) => (
          <AgentWizardLanguageCard
            key={lang}
            setCurrentLanguage={setCurrentLanguage}
            currentLanguage={currentLanguage}
            currentTypeOverride={currentTypeOverride}
            setCurrentTypeOverride={setCurrentTypeOverride}
            {...getLanguageInfo(lang)}
          />
        ))}
      </div>
      {
        <Stack
          flexDirection="column"
          className={css`
            margin-top: 16px;
            border-radius: 4px;
            padding: 24px;
            background-color: ${theme.color.teal10};
          `}
        >
          <Text font="body">
            {intl.formatMessage(
              { id: 'detail_selectLanguageDocumentation' },
              {
                ...formatStyles.doclink({
                  path:
                    getLanguageInfo(currentLanguage)?.learnMorePath ??
                    'agents.html'
                })
              }
            )}
          </Text>
        </Stack>
      }
    </Stack>
  )
}
